/**
 * Traduz `ScreenNode<'datagrid'>` para as props que o `DataGridComponent` espera.
 *
 * Função pura, testável sem DOM — de propósito, porque é aqui que mora o risco.
 * O `data-grid` recebe `@Input() props: any`, ou seja, o contrato é um objeto sem
 * tipo: um nome errado não quebra a compilação, quebra a tela do usuário. Um
 * teste sobre a conversão é a única rede possível.
 *
 * Os nomes NÃO batem entre os dois lados, e isso é intencional dos dois lados:
 *
 *   modelo novo          -> props do data-grid
 *   paginated            -> enablePagination
 *   filterable           -> enableFilters
 *   sortable             -> enableSorting
 *   enableCrud           -> enableCRUD          (repare no CRUD maiúsculo)
 *   masterDetail.enabled -> enableMasterDetail
 *   masterDetail.panelIds-> masterDetailFormPanels
 *   binding.dataPath     -> dataPath
 *
 * O modelo novo nomeia por PROPRIEDADE (`paginated`), o antigo por INTERRUPTOR
 * (`enablePagination`). Manter os dois vocabulários e traduzir num lugar só é
 * melhor que arrastar o vocabulário antigo para o modelo — que é o que faria a
 * migração ficar presa a ele para sempre.
 */
import { ScreenNode } from '../../model/node-props';
import { TranslateFn, toLegacyColumns } from './grid-columns';

/**
 * O objeto que o `data-grid` recebe.
 *
 * Declarado como interface, e não `any`, para que a conversão tenha ao menos um
 * lado tipado. O `data-grid` continua recebendo `any` — trocar a assinatura dele
 * é Fase 6.
 */
export interface LegacyGridProps {
  dataPath?: string;
  binding?: unknown;
  columns?: readonly unknown[];

  enablePagination?: boolean;
  pageSize?: number;
  maxVisibleRows?: number;
  enableFilters?: boolean;
  enableSorting?: boolean;
  enableCRUD?: boolean;
  selectionMode?: string;

  viewMode?: string;
  drawerWidth?: number;

  enableMasterDetail?: boolean;
  masterDetailFormPanels?: readonly string[];
  /** O componente lê o plural, mas há caminho legado que lê o singular. */
  masterDetailFormPanel?: string;

  autoAddFirstRow?: boolean;

  /** Titulo ja TRADUZIDO. O componente so sabe exibir texto pronto. */
  label?: string;
}

export function nodeToGridProps(
  node: ScreenNode<'datagrid'>,
  translate?: TranslateFn
): LegacyGridProps {
  const props = node.props;
  const legacy: LegacyGridProps = {};

  if (node.binding?.dataPath !== undefined) {
    legacy.dataPath = node.binding.dataPath;
  }

  // O titulo. O inspector oferece "Titulo da grade" desde sempre, e ate aqui
  // ele nao chegava ao componente — o usuario preenchia e nada acontecia.
  if (props.labelKey !== undefined && props.labelKey !== '') {
    legacy.label = translate === undefined
      ? props.labelKey
      : translate(props.labelKey);
  }

  if (props.columns !== undefined) {
    // NAO repassar como esta: os nomes divergem (`headerKey` x `header`,
    // `query` x `searchProperties`, `mappings` x `fieldMappings`), e repassar
    // deixava a grade com o cabecalho em branco em toda coluna, sem erro
    // nenhum no console. Ver grid-columns.ts.
    legacy.columns = toLegacyColumns(props.columns, translate);
  }

  // --- interruptores de comportamento
  if (props.paginated !== undefined) {
    legacy.enablePagination = props.paginated;
  }
  if (props.pageSize !== undefined) {
    legacy.pageSize = props.pageSize;
  }
  if (props.maxVisibleRows !== undefined) {
    legacy.maxVisibleRows = props.maxVisibleRows;
  }
  if (props.filterable !== undefined) {
    legacy.enableFilters = props.filterable;
  }
  if (props.sortable !== undefined) {
    legacy.enableSorting = props.sortable;
  }
  if (props.enableCrud !== undefined) {
    legacy.enableCRUD = props.enableCrud;
  }
  if (props.selectionMode !== undefined) {
    legacy.selectionMode = props.selectionMode;
  }

  // --- forma de exibição
  if (props.viewMode !== undefined) {
    legacy.viewMode = props.viewMode;
  }
  if (props.drawerWidth !== undefined) {
    legacy.drawerWidth = props.drawerWidth;
  }

  // --- mestre-detalhe
  if (props.masterDetail !== undefined) {
    legacy.enableMasterDetail = props.masterDetail.enabled;

    const panelIds = props.masterDetail.panelIds;
    if (Array.isArray(panelIds) && panelIds.length > 0) {
      legacy.masterDetailFormPanels = panelIds;
      // O primeiro painel também vai no singular: há caminho no componente que
      // ainda lê `masterDetailFormPanel`, e omitir faria mestre-detalhe de um
      // painel só parar de funcionar sem erro nenhum.
      legacy.masterDetailFormPanel = panelIds[0];
    }
  }

  if (props.autoAddFirstRow !== undefined) {
    legacy.autoAddFirstRow = props.autoAddFirstRow;
  }

  return legacy;
}

/**
 * O caminho efetivo do array quando a grade está dentro de uma aba.
 *
 * O `data-grid` tem uma entrada separada para isso (`effectiveDataPath`) porque
 * no modelo antigo a grade filha guardava só o caminho do pai, e o canvas
 * calculava o resto. No modelo novo o nó já carrega o caminho completo em
 * `binding.dataPath`, então esta função existe só para o caso em que quem chama
 * precise sobrescrever — e devolve `undefined` quando não há o que sobrescrever,
 * que é o normal.
 */
export function effectiveDataPathOf(node: ScreenNode<'datagrid'>): string | undefined {
  return node.binding?.dataPath;
}
